import { useState, useEffect } from 'react';
import type { FieldIssue } from '../../types';
import type { Campaign, ValidationFeedback } from '@/api/campaigns';
import {
  CharCount,
  FieldLabel,
  InlineIssue,
  Field,
  UploadZone,
  AssetPreview,
  AssetPreviewFromUrl,
  inputClass,
  issueBorder,
} from './shared';
import { GearExchangeModal, type GXImportedFile } from '../gear-exchange/GearExchangeModal';
import { GearExchangeImportButton } from '../gear-exchange/GearExchangeImportButton';

// ─── Constants ────────────────────────────────────────────────────────────────

const AD_TEXT_LIMIT = 100;

const CTA_OPTIONS = [
  'Apply Now',
  'Book Now',
  'Contact Us',
  'Download',
  'Get Quote',
  'Learn More',
  'Listen Now',
  'Order Now',
  'Play Game',
  'Shop Now',
  'Sign Up',
  'Subscribe',
  'Visit Store',
  'Watch More',
];

// ─── Main component ───────────────────────────────────────────────────────────

interface TikTokDetailsInitialValues {
  adText?: string;
  cta?: string;
  url?: string;
  legal?: string;
  existingVideoUrl?: string;
  videoContentType?: string | null;
}

interface TikTokDetailsProps {
  campaignId?: string;
  fordshareIds?: string[] | null;
  campaign?: Campaign | null;
  showErrors: boolean;
  showWarnings: boolean;
  fieldIssues: Record<string, FieldIssue>;
  file9x16: File | null;
  onFile9x16: (f: File | null) => void;
  onAdTextChange: (v: string) => void;
  onCtaChange: (v: string) => void;
  onUrlChange?: (v: string) => void;
  onLegalChange?: (v: string) => void;
  /** Called when the user confirms deletion of the existing saved video */
  onExistingVideoClear?: () => void;
  initialValues?: TikTokDetailsInitialValues;
  feedback?: Record<string, ValidationFeedback>;
  feedbackPending?: Set<string>;
  onAIFeedback?: (
    warningKey: string,
    warningMessage: string,
    action: 'accepted' | 'dismissed',
  ) => void;
  usedInVariants?: Map<string, number[]>;
  gxData?: import('@/api/gearExchange').GXMultiSearchResponse | null;
  gxLoading?: boolean;
  gxError?: boolean;
}

export function TikTokDetails({
  campaignId,
  fordshareIds,
  campaign,
  showErrors,
  showWarnings,
  fieldIssues,
  file9x16,
  onFile9x16: setFile9x16,
  onAdTextChange,
  onCtaChange,
  onUrlChange,
  onLegalChange,
  onExistingVideoClear,
  initialValues,
  feedback,
  feedbackPending,
  onAIFeedback,
  usedInVariants,
  gxData,
  gxLoading,
  gxError,
}: TikTokDetailsProps) {
  const [adText, setAdText] = useState(initialValues?.adText ?? '');
  const [destinationUrl, setDestinationUrl] = useState(initialValues?.url ?? '');
  const [cta, setCta] = useState(initialValues?.cta ?? 'Learn More');
  const [legal, setLegal] = useState(initialValues?.legal ?? '');
  const [clearedVideo, setClearedVideo] = useState(false);
  const [pendingClear, setPendingClear] = useState(false);

  // Gear Exchange state
  const [showGxModal, setShowGxModal] = useState(false);

  // Sync initial values to parent save state and mockup on mount
  useEffect(() => {
    onAdTextChange(adText);
    onCtaChange(cta);
    onUrlChange?.(destinationUrl);
    onLegalChange?.(legal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {/* Confirmation modal — shown before permanently deleting a saved video */}
      {pendingClear && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-80 bg-white rounded-xl border border-slate-200 shadow-xl flex flex-col overflow-hidden">
            <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl flex-shrink-0">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                Delete Video
              </p>
            </div>
            <div className="px-6 py-5">
              <p className="text-sm text-slate-600">
                Are you sure you want to remove the saved video? You will still need to press Save
                to apply this change.
              </p>
              <div className="flex gap-2 mt-5 justify-end">
                <button
                  onClick={() => setPendingClear(false)}
                  className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setClearedVideo(true);
                    onExistingVideoClear?.();
                    setPendingClear(false);
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto hover-scroll px-6 py-5 space-y-6 min-h-0">
        {/* ── About ───────────────────────────────────────────────────── */}
        <div className="border-l-2 border-[#0014cc]/30 pl-3">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
            About this format
          </p>
          <p className="text-sm text-slate-600 leading-relaxed">
            A <span className="font-medium text-slate-700">9:16 vertical video</span> that appears
            natively in the TikTok For You page feed. The ad plays full-screen between organic
            content, so your video needs to hook viewers in the first 3 seconds. The display name,
            ad text, and CTA overlay the video at the bottom.{' '}
            <a
              href="https://ads.tiktok.com/help/article/tiktok-reservation-topview"
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-400 hover:text-slate-600 underline underline-offset-2 decoration-slate-300"
            >
              TikTok ad specs ↗
            </a>
          </p>
        </div>

        {/* ── Gear Exchange Import ──────────────────────────────────── */}
        {
          <>
            <GearExchangeImportButton
              totalAssets={gxData?.total_assets ?? null}
              loading={gxLoading}
              error={gxError}
              fordshareIds={fordshareIds ?? undefined}
              onClick={() => setShowGxModal(true)}
            />

            {showGxModal && gxData && campaign && campaignId && (
              <GearExchangeModal
                data={gxData}
                campaign={campaign}
                campaignId={campaignId}
                formatLabel="TikTok"
                zoneLimits={[
                  {
                    ratio: '9x16',
                    limit: 1,
                    existing:
                      file9x16 || (initialValues?.existingVideoUrl && !clearedVideo) ? 1 : 0,
                  },
                ]}
                onClose={() => setShowGxModal(false)}
                usedInVariants={usedInVariants}
                onImport={(files: GXImportedFile[]) => {
                  const file = files[0];
                  if (file) {
                    setFile9x16(file.file);
                    setClearedVideo(true);
                  }
                }}
              />
            )}
          </>
        }

        {/* ── Asset ───────────────────────────────────────────────────── */}
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
            9:16 Video
          </p>
          <div className="border-b border-slate-200 mb-3" />
          {(() => {
            const fi = fieldIssues['asset_files'];
            const it =
              fi?.errors.length && showErrors
                ? ('error' as const)
                : fi?.warnings.length && showWarnings
                  ? ('warning' as const)
                  : undefined;
            return (
              <>
                {file9x16 ? (
                  <AssetPreview
                    file={file9x16}
                    onRemove={() => setFile9x16(null)}
                    ratio="9x16"
                    issueType={it}
                  />
                ) : initialValues?.existingVideoUrl && !clearedVideo ? (
                  <AssetPreviewFromUrl
                    url={initialValues.existingVideoUrl}
                    contentType={initialValues.videoContentType}
                    onRemove={() => setPendingClear(true)}
                    ratio="9x16"
                    issueType={it}
                  />
                ) : (
                  <UploadZone
                    onFile={setFile9x16}
                    hint="Click to upload 9:16 video"
                    accept="video/*"
                    issueType={it}
                  />
                )}
                <InlineIssue
                  issue={fi}
                  showErrors={showErrors}
                  showWarnings={showWarnings}
                  feedback={feedback}
                  feedbackPending={feedbackPending}
                  onFeedback={onAIFeedback}
                />
              </>
            );
          })()}
        </div>

        {/* ── Copy ────────────────────────────────────────────────────── */}
        <div className="space-y-5">
          {/* Ad Text */}
          <div>
            <FieldLabel
              label="Ad Text"
              right={<CharCount value={adText} limit={AD_TEXT_LIMIT} />}
              tooltip="The caption that appears below the video. Keep it short and punchy — the first line is the most visible before the viewer taps to expand."
            />
            <textarea
              value={adText}
              onChange={(e) => {
                setAdText(e.target.value);
                onAdTextChange(e.target.value);
              }}
              maxLength={AD_TEXT_LIMIT}
              placeholder="Write your ad caption..."
              rows={3}
              className={`${inputClass} resize-none ${issueBorder(fieldIssues['primary_text'], showErrors, showWarnings)}`}
            />
            <InlineIssue
              issue={fieldIssues['primary_text']}
              showErrors={showErrors}
              showWarnings={showWarnings}
              feedback={feedback}
              feedbackPending={feedbackPending}
              onFeedback={onAIFeedback}
            />
          </div>

          {/* 2-col: Destination URL + CTA */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-5">
            <div>
              <FieldLabel
                label="Destination URL"
                tooltip="Where users are taken when they tap the CTA button. Make sure it matches the message in your video."
              />
              <input
                type="url"
                value={destinationUrl}
                onChange={(e) => {
                  setDestinationUrl(e.target.value);
                  onUrlChange?.(e.target.value);
                }}
                placeholder="https://www.ford.com/..."
                className={`${inputClass} ${issueBorder(fieldIssues['destination_url'], showErrors, showWarnings)}`}
              />
              <InlineIssue
                issue={fieldIssues['destination_url']}
                showErrors={showErrors}
                showWarnings={showWarnings}
                feedback={feedback}
                feedbackPending={feedbackPending}
                onFeedback={onAIFeedback}
              />
            </div>

            <div>
              <FieldLabel
                label="Call to Action"
                tooltip="The button label overlaid on the ad. Choose the option that best matches the action you want viewers to take."
              />
              <select
                value={cta}
                onChange={(e) => {
                  setCta(e.target.value);
                  onCtaChange(e.target.value);
                }}
                className={`${inputClass} cursor-pointer`}
              >
                {CTA_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Legal */}
          <Field
            label="Legal"
            tooltip="Required in some markets (e.g. France) for regulated categories. Add any mandatory disclaimer copy here — it will not appear in the mockup but will be submitted with the ad."
            fieldKey="legal"
            fieldIssues={fieldIssues}
            showErrors={showErrors}
            showWarnings={showWarnings}
            feedback={feedback}
            feedbackPending={feedbackPending}
            onFeedback={onAIFeedback}
            help={<>Enter "N/A" if your market does not require a legal disclaimer.</>}
          >
            <textarea
              value={legal}
              onChange={(e) => {
                setLegal(e.target.value);
                onLegalChange?.(e.target.value);
              }}
              placeholder="Legal disclaimer — required for some markets (e.g. FR)..."
              rows={5}
              className={`${inputClass} resize-none ${issueBorder(fieldIssues['legal'], showErrors, showWarnings)}`}
            />
          </Field>
        </div>
      </div>
    </>
  );
}
